import React, { forwardRef, useState, useImperativeHandle } from 'react'
import { accessStorage, errorMessage } from './institutionalServiceHelper.js'
import InputGroup from '../../../../components/EmailSubscriptionCenter/SignIn/InputGroup.jsx'
import PasswordValidator from '../../../../components/Login/Modules/PasswordValidator/PasswordValidator.jsx'
import FormNotification from '../../../../components/SelfService/FormNotification.jsx'

const InstitutionalChangePassword = forwardRef(
  function InstitutionalChangePassword(props, ref) {
    const { onSuccessChangePassword, fromForgotPassword } = props
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordValid, setPasswordValid] = useState(true)
    const [showPasswordValidator, setShowPasswordValidator] = useState(false)
    const [formErrorMessage, setFormErrorMessage] = useState('')
    const [formValidation, setFormValidation] = useState({
      'current-password': {
        error: false,
        errorMessage: '',
      },
      'login-new-password': {
        error: false,
        errorMessage: '',
      },
      'login-confirm-new-password': {
        error: false,
        errorMessage: 'Password error',
      },
    })

    useImperativeHandle(
      ref,
      () => ({
        showDefaultMessage(message) {
          setFormErrorMessage(message ?? errorMessage.defaultError)
        },
      }),
      []
    )

    const RESPONSE_CODES = {
      INVALID_CURRENT_PASSWORD: '7006-4033',
      INVALID_NEW_PASSWORD: '7006-4034',
      INVALID_NEW_PASSWORD_2: '7005-4034',
      INVALID_TOKEN: '7006-1014',
      SSO_LOCKED: '7006-4036',
    }

    const onChangeCurrentPassword = (event) => {
      const _formValidation = { ...formValidation }
      _formValidation['current-password'].error = false
      setFormValidation(_formValidation)
      setCurrentPassword(event.target.value)
      setFormErrorMessage('')
    }

    const onChangeNewPassword = (event) => {
      const _formValidation = { ...formValidation }
      _formValidation['login-new-password'].error = false
      setFormValidation(_formValidation)
      setShowPasswordValidator(!!event.target.value)
      setNewPassword(event.target.value)
      setFormErrorMessage('')
    }

    const onChangeConfirmPassword = (event) => {
      const _formValidation = { ...formValidation }
      _formValidation['login-confirm-new-password'].error = false
      setFormValidation(_formValidation)
      setConfirmPassword(event.target.value)
      setFormErrorMessage('')
    }

    const onNextClicked = async () => {
      if (!currentPassword && !fromForgotPassword) {
        const _formValidation = { ...formValidation }
        _formValidation['current-password'].error = true
        _formValidation['current-password'].errorMessage = 'Enter your password'
        setFormValidation(_formValidation)
      } else if (!newPassword) {
        const _formValidation = { ...formValidation }
        _formValidation['login-new-password'].error = true
        _formValidation['login-new-password'].errorMessage =
          'Enter new password'
        setFormValidation(_formValidation)
      } else if (newPassword !== confirmPassword) {
        const _formValidation = { ...formValidation }
        _formValidation['login-confirm-new-password'].error = true
        _formValidation['login-confirm-new-password'].errorMessage =
          'Passwords do not match'
        setFormValidation(_formValidation)
      } else {
        const _formValidation = { ...formValidation }
        _formValidation['login-confirm-new-password'].error = false
        setFormValidation(_formValidation)
        onSuccessChangePassword(
          {
            currentPassword,
            newPassword,
          },
          handleError
        )
      }
    }

    const handleError = (errorCode, message) => {
      const _formValidation = { ...formValidation }
      switch (errorCode) {
        case RESPONSE_CODES.INVALID_CURRENT_PASSWORD:
          _formValidation['current-password'].error = true
          _formValidation['current-password'].errorMessage =
            'The password you entered is incorrect. Please try again.'
          setFormValidation(_formValidation)
          break
        case RESPONSE_CODES.INVALID_NEW_PASSWORD:
        case RESPONSE_CODES.INVALID_NEW_PASSWORD_2:
          _formValidation['login-confirm-new-password'].error = true
          _formValidation['login-confirm-new-password'].errorMessage =
            'Please enter a valid password. Your password cannot  match your most recent password, cannot contain the word "password" or any variation of it and cannot be too common.'
          setFormValidation(_formValidation)
          break
        default:
          setFormErrorMessage(message)
      }
    }

    const userId = accessStorage.getUserInfo()?.userId ?? ''

    return (
      <div
        className="u-sizeConstrained"
        data-analytics-login-step="Change Password"
      >
        <h2 className="u-marginTop2gu u-marginBottom1gu">
          PLEASE CREATE NEW PASSWORD: <strong>{userId}</strong>
        </h2>
        <div className="Grid">
          <div className="u-sizeFull u-lg-after1of12 u-lg-size5of12 Content u-marginBottom6gu u-lg-marginBottom0">
            {formErrorMessage && (
              <FormNotification content={formErrorMessage} isHtml={true} />
            )}
            <fieldset>
              {!fromForgotPassword && (
                <InputGroup
                  formValidation={formValidation}
                  fieldName="current-password"
                  fieldId="current-password"
                  label="Current password"
                  handleInputChange={onChangeCurrentPassword}
                  compact={false}
                  type="password"
                  defaultValue={currentPassword}
                />
              )}
              <InputGroup
                formValidation={formValidation}
                fieldName="login-new-password"
                fieldId="login-new-password"
                label="New password"
                handleInputChange={onChangeNewPassword}
                compact={false}
                type="password"
                defaultValue={newPassword}
              />
              {showPasswordValidator && (
                <div
                  className={
                    'u-marginBottom5gu u-padding2gu u-bgColorWhite u-borderColorNeutralLight u-borderBottom u-borderTop u-borderLeft u-borderRight' +
                    (passwordValid ? ' u-hidden' : '')
                  }
                  style={{ transform: 'translateY(-36px)' }}
                >
                  <PasswordValidator
                    username={userId}
                    passwordInput={newPassword}
                    onValidate={(v) => setPasswordValid(v)}
                  />
                </div>
              )}
              <InputGroup
                formValidation={formValidation}
                fieldName="login-confirm-new-password"
                fieldId="login-confirm-new-password"
                label="Confirm new password"
                handleInputChange={onChangeConfirmPassword}
                compact={false}
                type="password"
                defaultValue={confirmPassword}
              />
              <button
                onClick={onNextClicked}
                disabled={formErrorMessage}
                className="Button Button--primary Button--block u-marginBottom4gu u-textNoTransform"
                data-tracking="primary"
              >
                Next
              </button>
            </fieldset>
          </div>
        </div>
      </div>
    )
  }
)
export default InstitutionalChangePassword
